import React from 'react';
import {
  AUDIT_FACTS,
  AUDIT_GETS,
  EXAMPLES,
  FAQS,
  FIT_FOR,
  FIT_NOT,
  SAMPLE_SCORECARD,
  STALL_POINTS,
  STEPS,
  SYMPTOMS,
} from './content';
import { Arrow, CtaButton, Kicker, Reveal, Scorecard } from './parts';

const Check = ({ className = '' }) => (
  <svg className={className} width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="8.25" stroke="currentColor" strokeOpacity=".35" />
    <path d="M5.5 9.2l2.3 2.3 4.7-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Cross = ({ className = '' }) => (
  <svg className={className} width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="8.25" stroke="currentColor" strokeOpacity=".35" />
    <path d="M6.3 6.3l5.4 5.4M11.7 6.3l-5.4 5.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

/* ---------------------------------------------------------------- HERO */
export function Hero() {
  return (
    <section id="top" className="rb-grain overflow-x-clip pt-[120px] sm:pt-[140px] pb-16 sm:pb-24 px-5 sm:px-8">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="rb-enter" style={{ '--d': '0s' }}>
            <span className="rb-pill">
              <span className="dot" />
              <span className="sm:hidden">Free AI Business Audit</span>
              <span className="hidden sm:inline">AI consulting · Free AI Business Audit</span>
            </span>
          </div>

          <h1
            className="rb-enter rb-display mt-7 text-[2.25rem] leading-[1.08] sm:text-[3.1rem] lg:text-[3.55rem] font-[420] text-ink"
            style={{ '--d': '0.08s' }}
          >
            Before you automate anything,{' '}
            <span className="rb-highlight text-brand">find out what’s actually slowing you down.</span>
          </h1>

          <p
            className="rb-enter mt-7 text-[1.08rem] sm:text-[1.18rem] leading-relaxed text-ink-soft max-w-[36rem]"
            style={{ '--d': '0.2s' }}
          >
            A free AI Business Audit that maps how your business really runs — the follow-ups, reports, re-typing, and
            scheduling eating your week — and tells you plainly where AI is worth it, and where it isn’t.
          </p>

          <div id="hero-cta" className="rb-enter mt-9 flex flex-wrap items-center gap-x-7 gap-y-4" style={{ '--d': '0.3s' }}>
            <CtaButton />
            <a href="#audit" className="rb-link rb-tap text-[0.95rem]">
              See what you get
            </a>
          </div>
          <p className="rb-enter mt-6 text-[0.9rem] text-ink-mute" style={{ '--d': '0.45s' }}>
            It starts with a free 30-minute call.
          </p>
        </div>

        <div className="rb-enter rb-enter-tilt lg:col-span-5 relative" style={{ '--d': '0.25s' }}>
          <div className="absolute -inset-6 -z-10 rounded-[28px] bg-brand-mist/70 blur-2xl" aria-hidden="true" />
          <Scorecard
            title="AI Business Audit · Scorecard"
            subtitle={SAMPLE_SCORECARD.business}
            scores={SAMPLE_SCORECARD.scores}
            leak={SAMPLE_SCORECARD.leak}
            footnote="Sample for illustration — every audit is scored on your own business."
          />
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ SYMPTOMS */
export function Symptoms() {
  return (
    <section className="px-5 sm:px-8 py-16 sm:py-24 bg-paper-deep/60 border-y border-ink/10">
      <div className="max-w-6xl mx-auto">
        <Reveal className="text-center">
          <Kicker center>Sound familiar?</Kicker>
          <h2 className="rb-display mt-6 text-[1.85rem] sm:text-[2.9rem] leading-[1.08] max-w-4xl mx-auto font-[420]">
            The week keeps filling up with work <span className="rb-accent">that isn’t the work.</span>
          </h2>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {SYMPTOMS.map((s, i) => (
            <Reveal key={s.quote} delay={i * 0.05} className="rb-card p-5 sm:p-6 flex flex-col justify-between sm:min-h-[150px]">
              <p className="rb-display rb-quote text-[1.15rem] leading-snug text-ink">{s.quote}</p>
              <p className="mt-3 sm:mt-4 text-[0.9rem] text-ink-mute">{s.note}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-10 flex flex-col sm:flex-row items-center justify-center text-center sm:text-left gap-5 sm:gap-8">
          <p className="text-[1.05rem] text-ink-soft max-w-xl">
            If two or three of these sound like your week, an audit will almost certainly find something worth fixing.
          </p>
          <CtaButton small />
        </Reveal>
      </div>
    </section>
  );
}

/* --------------------------------------------------------- WHY STALL */
export function WhyStall() {
  return (
    <section className="rb-night px-5 sm:px-8 py-16 sm:py-24">
      <div className="max-w-6xl mx-auto">
        <Reveal className="text-center">
          <Kicker center>Why AI projects stall</Kicker>
          <h2 className="rb-display mt-6 text-[1.85rem] sm:text-[2.9rem] leading-[1.08] max-w-4xl mx-auto font-[400] text-white">
            It’s rarely the AI. <span className="rb-accent">It’s what the AI is sitting on.</span>
          </h2>
        </Reveal>
        <div className="mt-10 sm:mt-12 grid md:grid-cols-3 gap-8">
          {STALL_POINTS.map((p, i) => (
            <Reveal key={p.title} delay={i * 0.08} className="border-t border-white/15 pt-6">
              <div className="text-[0.9rem] font-semibold text-brand-light">0{i + 1}</div>
              <h3 className="rb-display text-[1.45rem] mt-3 text-white">{p.title}</h3>
              <p className="mt-3 text-[0.98rem] leading-relaxed text-[#b9bcd4]">{p.body}</p>
            </Reveal>
          ))}
        </div>
        <Reveal className="mt-12 sm:mt-14 grid lg:grid-cols-12 gap-6 lg:gap-8 items-end">
          <blockquote className="lg:col-span-7 rb-display rb-quote text-[1.6rem] sm:text-[2rem] leading-[1.2] text-white">
            “AI doesn’t fix a broken process. It runs it faster.”
          </blockquote>
          <p className="lg:col-span-5 text-[1rem] leading-relaxed text-[#b9bcd4]">
            That’s why every engagement starts with the foundation — how your data and processes actually work —
            before anything gets automated. It’s where senior enterprise data and IT experience earns its keep.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- AUDIT */
export function Audit() {
  return (
    <section id="audit" className="px-5 sm:px-8 py-16 sm:py-24">
      <div className="max-w-6xl mx-auto">
        <Reveal className="text-center">
          <Kicker center>The AI Business Audit</Kicker>
          <h2 className="rb-display mt-6 text-[1.85rem] sm:text-[2.9rem] leading-[1.08] max-w-4xl mx-auto font-[420]">
            A clear picture of your business, <span className="rb-accent">on paper, before you spend a dollar.</span>
          </h2>
        </Reveal>
        <div className="mt-10 sm:mt-12 grid lg:grid-cols-12 gap-8 lg:gap-14">
          <Reveal className="lg:col-span-7">
            <div className="rb-label mb-5">What you get</div>
            <ul className="divide-y divide-ink/10 border-y border-ink/10">
              {AUDIT_GETS.map((g) => (
                <li key={g} className="flex gap-4 py-4 text-[1.03rem] leading-relaxed text-ink">
                  <Check className="text-brand mt-1 shrink-0" />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.1}>
            <div className="rb-card p-7">
              <div className="rb-label mb-4">The fine print</div>
              <dl>
                {AUDIT_FACTS.map((f) => (
                  <div key={f.k} className="flex items-baseline justify-between gap-6 py-3.5 border-b border-ink/10 last:border-0">
                    <dt className="text-[0.9rem] text-ink-mute">{f.k}</dt>
                    <dd className="text-right font-semibold text-ink">{f.v}</dd>
                  </div>
                ))}
              </dl>
              <div className="mt-6">
                <CtaButton className="w-full justify-center" />
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- PROCESS */
// Desktop: a horizontal 5-step timeline. Phones/tablets: a vertical list on a rail.
export function Process() {
  return (
    <section id="how" className="px-5 sm:px-8 py-14 sm:py-24 bg-paper-deep/60 border-y border-ink/10">
      <div className="max-w-6xl mx-auto">
        <Reveal className="text-center">
          <Kicker center>How it works</Kicker>
          <h2 className="rb-display mt-6 text-[1.85rem] sm:text-[2.9rem] leading-[1.08] max-w-4xl mx-auto font-[420]">
            We run the process. <span className="rb-accent">You show up for about two hours.</span>
          </h2>
        </Reveal>

        {/* Desktop: brackets over the timeline — steps 01–03 are the free audit, 04–05 only if the client continues. */}
        <div className="hidden lg:grid grid-cols-5 gap-5 mt-14" aria-hidden="true">
          <div className="col-span-3 rb-phase">The free audit</div>
          <div className="col-span-2 rb-phase rb-phase-optional">If you choose to continue</div>
        </div>

        <ol className="rb-steps mt-8 lg:mt-6 grid lg:grid-cols-5 gap-0 lg:gap-5">
          {STEPS.map((s, i) => (
            <React.Fragment key={s.n}>
            {s.optional && !STEPS[i - 1].optional && (
              <li className="rb-step-break lg:hidden" aria-hidden="true">
                If you choose to continue
              </li>
            )}
            <Reveal
              as="li"
              delay={i * 0.06}
              className={`rb-step relative pl-12 lg:pl-0 pb-6 lg:pb-0 ${s.optional ? 'is-optional' : ''} ${
                STEPS[i + 1]?.optional ? 'leads-optional' : ''
              }`}
            >
              <span className="rb-step-dot" aria-hidden="true">
                {s.n}
              </span>
              <div className="lg:mt-7">
                <div className="text-[0.9rem] font-semibold text-brand">{s.time}</div>
                <h3 className="rb-display text-[1.15rem] lg:text-[1.2rem] leading-snug mt-1 lg:mt-1.5 text-ink">{s.title}</h3>
                <p className="mt-1.5 text-[0.95rem] leading-normal lg:leading-relaxed text-ink-soft">{s.body}</p>
                <ul className="mt-2.5 flex flex-wrap lg:flex-col gap-x-4 gap-y-1">
                  {s.gets.map((g) => (
                    <li key={g} className="flex items-start gap-1.5 text-[0.9rem] leading-snug font-medium text-brand-deep">
                      <Check className="w-4 h-4 mt-[3px] shrink-0 text-brand" />
                      {g}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            </React.Fragment>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ EXAMPLES */
export function Examples() {
  return (
    <section className="px-5 sm:px-8 py-16 sm:py-24">
      <div className="max-w-6xl mx-auto">
        <Reveal className="text-center">
          <Kicker center>What an audit might find</Kicker>
          <h2 className="rb-display mt-6 text-[1.85rem] sm:text-[2.9rem] leading-[1.08] max-w-4xl mx-auto font-[420]">
            Sometimes the answer is AI. <span className="rb-accent">Sometimes it’s “not yet.”</span>
          </h2>
          <p className="mt-4 text-[0.9rem] leading-relaxed text-ink-mute">
            Illustrative examples of common situations — not client results.
          </p>
        </Reveal>

        <div className="mt-10 grid md:grid-cols-2 gap-4 sm:gap-5">
          {EXAMPLES.map((e, i) => (
            <Reveal key={e.who} delay={(i % 2) * 0.08} className="rb-card p-6 sm:p-7 flex flex-col">
              <div className="flex items-center justify-between gap-4">
                <span className="rb-label">{e.who}</span>
                <span
                  className={`text-[0.875rem] font-semibold px-3 py-1 rounded-full whitespace-nowrap ${
                    e.verdict === 'go' ? 'bg-brand-soft text-brand-deep' : 'bg-[#f6e3d8] text-[#9a3412]'
                  }`}
                >
                  {e.verdict === 'go' ? 'Go' : 'No-go'}
                </span>
              </div>
              <p className="rb-display rb-quote text-[1.22rem] leading-snug mt-4 text-ink">{e.heard}</p>
              <dl className="mt-4 space-y-3 text-[0.96rem] leading-relaxed">
                <div>
                  <dt className="rb-label mb-1">What we’d likely find</dt>
                  <dd className="text-ink-soft">{e.found}</dd>
                </div>
                <div>
                  <dt className="rb-label mb-1">What we’d recommend</dt>
                  <dd className="text-ink-soft">{e.rec}</dd>
                </div>
              </dl>
              <div className="mt-auto pt-4">
                <div className="rb-rule mb-3" />
                <span className={`font-semibold text-[0.92rem] ${e.verdict === 'go' ? 'text-brand' : 'text-[#9a3412]'}`}>
                  {e.verdictText}
                </span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ FIT CHECK */
export function FitCheck() {
  return (
    <section id="fit" className="px-5 sm:px-8 py-16 sm:py-24 bg-paper-deep/60 border-y border-ink/10">
      <div className="max-w-6xl mx-auto">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Kicker center>Fit check</Kicker>
          <h2 className="rb-display mt-6 text-[1.85rem] sm:text-[2.9rem] leading-[1.08] font-[420]">
            Is the audit <span className="rb-accent">right for you?</span>
          </h2>
        </Reveal>

        <div className="mt-10 sm:mt-12 grid lg:grid-cols-2 gap-4 sm:gap-5">
          <Reveal className="bg-white rounded-[18px] border border-ink/10 border-t-4 border-t-brand p-6 sm:p-8 shadow-[0_20px_40px_-30px_rgba(22,26,58,0.35)]">
            <h3 className="rb-display text-[1.5rem]">
              This is <span className="text-brand">for you</span> if…
            </h3>
            <ul className="mt-5 space-y-3">
              {FIT_FOR.map((f) => (
                <li key={f} className="flex gap-3.5 text-[1rem] leading-relaxed text-ink">
                  <Check className="text-brand mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.08} className="bg-white rounded-[18px] border border-ink/10 border-t-4 border-t-ink/20 p-6 sm:p-8 shadow-[0_20px_40px_-30px_rgba(22,26,58,0.35)]">
            <h3 className="rb-display text-[1.5rem]">
              This is <span className="text-ink-mute">not for you</span> if…
            </h3>
            <ul className="mt-5 space-y-3">
              {FIT_NOT.map((f) => (
                <li key={f} className="flex gap-3.5 text-[1rem] leading-relaxed text-ink-soft">
                  <Cross className="text-ink-mute mt-0.5 shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ FAQ */
export function Faq() {
  return (
    <section id="faq" className="px-5 sm:px-8 py-16 sm:py-24">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-10">
        <Reveal className="lg:col-span-4">
          <Kicker>Questions</Kicker>
          <h2 className="rb-display mt-6 text-[1.85rem] sm:text-[2.6rem] leading-[1.08] font-[420]">
            Straight answers, <span className="rb-accent">before you book.</span>
          </h2>
        </Reveal>
        <Reveal className="lg:col-span-8" delay={0.05}>
          <div className="border-t border-ink/15">
            {FAQS.map((f) => (
              <details key={f.q} className="rb-faq border-b border-ink/15 group">
                <summary className="flex items-center justify-between gap-6 py-5">
                  <span className="text-[1.08rem] font-semibold text-ink">{f.q}</span>
                  <span className="plus shrink-0 w-8 h-8 grid place-items-center rounded-full border border-ink/15 text-ink">
                    <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
                      <path d="M6 1v10M1 6h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </span>
                </summary>
                <p className="pb-6 -mt-1 pr-12 text-[1rem] leading-relaxed text-ink-soft">{f.a}</p>
              </details>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export { Arrow };
